import { useState, type ReactNode } from "react";
import { cn } from "../../utils/cn";
import { Button } from "../base/Button";

export interface StepperStep {
  id: string;
  title: string;
  subtitle?: string;
  content: ReactNode;
}

interface StepperProps {
  steps: StepperStep[];
  /** Validate the current step before advancing. Return an error string to block. */
  validateStep?: (stepIndex: number) => string | null;
  onComplete?: () => void;
  completeLabel?: string;
  hideNavigation?: boolean;
  /** Controlled mode — supply these to drive the current step from outside (e.g. edit links on the review step). */
  currentStep?: number;
  onStepChange?: (step: number) => void;
}

export function Stepper({ steps, validateStep, onComplete, completeLabel = "Finish", hideNavigation = false, currentStep, onStepChange }: StepperProps) {
  const [internal, setInternal] = useState(0);
  const current = currentStep ?? internal;

  const goTo = (next: number) => {
    if (onStepChange) onStepChange(next);
    else setInternal(next);
  };

  const [touched, setTouched] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const isLast = current === steps.length - 1;

  const goNext = () => {
    setError(null);
    const stepError = validateStep?.(current) ?? null;
    if (stepError) {
      setTouched((t) => (t.includes(current) ? t : [...t, current]));
      setError(stepError);
      return;
    }
    if (isLast) {
      onComplete?.();
      return;
    }
    goTo(current + 1);
  };

  const goBack = () => {
    setError(null);
    goTo(Math.max(0, current - 1));
  };

  return (
    <div className="min-w-0">
      <div className="overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <ol className="flex min-w-max items-center gap-1 sm:min-w-0 sm:gap-2">
          {steps.map((step, i) => {
            const state = i < current ? "done" : i === current ? "current" : "todo";
            const last = i === steps.length - 1;
            return (
              <li key={step.id} className={cn("flex items-center gap-2", !last && "flex-1")}>
                <button
                  type="button"
                  onClick={() => state === "done" && goTo(i)}
                  className={cn("flex items-center gap-2 rounded-lg p-1 text-left transition-colors", state === "done" ? "cursor-pointer hover:bg-background-100/80" : "cursor-default")}
                >
                  <span
                    className={cn(
                      "flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-bold transition-all sm:h-8 sm:w-8 sm:text-sm",
                      state === "done" && "bg-success-600 text-white shadow-sm",
                      state === "current" && "bg-primary-600 text-white shadow-sm ring-2 ring-primary-500/20",
                      state === "todo" && "border border-background-300 bg-background-50 text-foreground-400"
                    )}
                  >
                    {state === "done" ? <i className="ri-check-line text-sm" /> : i + 1}
                  </span>
                  <div className="hidden min-w-0 md:block">
                    <span className={cn("block truncate text-xs font-semibold sm:text-sm", state === "current" ? "text-foreground-950 font-bold" : state === "done" ? "text-foreground-700" : "text-foreground-400")}>
                      {step.title}
                    </span>
                    {step.subtitle ? (
                      <span className="block truncate text-[11px] text-foreground-400">{step.subtitle}</span>
                    ) : null}
                  </div>
                </button>
                {!last ? (
                  <div className={cn("hidden h-0.5 min-w-4 flex-1 rounded-full sm:block", i < current ? "bg-success-500" : "bg-background-200")} />
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mt-4 rounded-xl border border-background-200 bg-background-50 p-4 sm:p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between border-b border-background-100 pb-3 md:hidden">
          <span className="text-xs font-bold uppercase tracking-wider text-primary-600">
            Step {current + 1} of {steps.length}
          </span>
          <span className="text-sm font-semibold text-foreground-900">{steps[current].title}</span>
        </div>
        {steps[current].content}
        {error ? <p className="mt-4 rounded-lg bg-danger-500/10 p-2.5 text-xs font-medium text-danger-600">{error}</p> : null}
      </div>

      {!hideNavigation ? (
        <div className="mt-4 flex items-center justify-between">
          <Button variant="outline" onClick={goBack} disabled={current === 0} icon={<i className="ri-arrow-left-line" />}>
            Back
          </Button>
          <div className="text-xs text-foreground-500">
            Step {current + 1} of {steps.length}
          </div>
          <Button onClick={goNext} icon={<i className={isLast ? "ri-check-line" : "ri-arrow-right-line"} />}>
            {isLast ? completeLabel : "Next"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}